import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import toast, { Toaster } from 'react-hot-toast';
import { 
  User, 
  Mail, 
  Phone, 
  Eye, 
  EyeOff, 
  Lock, 
  Shield, 
  Clock, 
  Edit2,
  Building,
  Globe,
  Tag,
  MapPin,
  Save,
  X
} from 'lucide-react';
import { apiRequest } from '../utils/api';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f9fafb;
    color: #111827;
    transition: all 0.3s ease;
    min-height: 100vh;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: #f9fafb;
  padding: 2rem 1rem;

  @media (max-width: 640px) {
    padding: 1rem 0.5rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 112rem;
  margin: 0 auto;
`;

const ProfileHeader = styled.div`
  background: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const HeaderGradient = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  height: 8rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ProfileContent = styled.div`
  position: relative;
  padding: 0 2rem 2rem;

  @media (max-width: 640px) {
    padding: 0 1rem 1.5rem;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: -4rem;
  margin-bottom: 1.5rem;

  @media (min-width: 640px) {
    flex-direction: row;
    align-items: flex-end;
    text-align: left;
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;

  @media (min-width: 640px) {
    margin-bottom: 0;
    margin-right: 1.5rem;
  }
`;

const Avatar = styled.img`
  width: 8rem;
  height: 8rem;
  border-radius: 50%;
  border: 4px solid #ffffff;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  background: #ffffff;
`;

const OnlineIndicator = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 0.5rem;
  width: 1.5rem;
  height: 1.5rem;
  background: #10b981;
  border-radius: 50%;
  border: 2px solid #ffffff;
`;

const UserDetails = styled.div`
  flex: 1;
  text-align: center;

  @media (min-width: 640px) {
    text-align: left;
  }
`;

const UserName = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const UserEmail = styled.p`
  font-size: 1.125rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: flex-start;
  }
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  color: #6b7280;

  svg {
    color: #1e90ff;
  }
`;

const ActionButtons = styled.div`
  margin-top: 1rem;

  @media (min-width: 640px) {
    margin-top: 0;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: #ffffff;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);

  @media (max-width: 640px) {
    padding: 1.5rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: #1e90ff;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #ffffff;
  color: #111827;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1e90ff;
    box-shadow: 0 0 0 3px rgba(30, 144, 255, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Value = styled.div`
  padding: 0.75rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;

  ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
          color: white;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          }
        `;
      case 'secondary':
        return `
          background: #6b7280;
          color: white;
          
          &:hover {
            background: #4b5563;
          }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          
          &:hover {
            background: #059669;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: #1e90ff;
          border: 2px solid #1e90ff;
          
          &:hover {
            background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
            color: white;
          }
        `;
      default:
        return `
          background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
          color: white;
          
          &:hover {
            transform: translateY(-1px);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.full-width {
    width: 100%;
    justify-content: center;
  }
`;

const StatusItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusLabel = styled.span`
  color: #6b7280;
  font-size: 0.875rem;
`;

const StatusValue = styled.div`
  text-align: right;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  color: white;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 50;
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 28rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
`;

const CloseButton = styled.button`
  padding: 0.25rem;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const PasswordField = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const PasswordInput = styled(Input)`
  padding-right: 3rem;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    color: #111827;
  }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
`;

const LoadingContent = styled.div`
  text-align: center;
`;

const LoadingSpinner = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 1rem 1.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: white;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  cursor: not-allowed;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 0.75rem;
    height: 1.25rem;
    width: 1.25rem;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

function ProfilePage() {
  const [userData, setUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isOldPasswordVisible, setIsOldPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const userTimeZone = userData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return new Intl.DateTimeFormat('en-US', {
      timeZone: userTimeZone,
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(dateString));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await apiRequest('/api/auth/settings/profile', {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch profile. Status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
        setEditedData(data);
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      const response = await apiRequest('/api/auth/settings/profile/update', {
        method: 'POST',
        body: JSON.stringify(editedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const updatedData = await response.json();
      setUserData(updatedData);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Failed to update profile');
    }
  };

  const handleCancelEdit = () => {
    setEditedData(userData);
    setIsEditing(false);
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }
    
    try {
      const response = await apiRequest('/api/auth/settings/change-password', {
        method: 'POST',
        body: JSON.stringify({
          old_password: oldPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to change password');
      }

      setIsPasswordModalOpen(false);
      toast.success('Password changed successfully!');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    }
  };

  const closeModal = () => {
    setIsPasswordModalOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  if (loading) {
    return (
      <>
        <GlobalStyle />
        <LoadingContainer>
          <LoadingContent>
            <LoadingSpinner>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"></circle>
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" opacity="0.75"></path>
              </svg>
              Loading...
            </LoadingSpinner>
          </LoadingContent>
        </LoadingContainer>
      </>
    );
  }

  if (!userData) {
    return (
      <>
        <GlobalStyle />
        <LoadingContainer>
          <LoadingContent>
            <p>Failed to load profile data. Please try refreshing the page.</p>
          </LoadingContent>
        </LoadingContainer>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Container>
        <ContentWrapper>
          {/* Profile Header */}
          <ProfileHeader>
            <HeaderGradient />
            <ProfileContent>
              <ProfileInfo>
                <AvatarContainer>
                  <Avatar 
                    src={`https://api.dicebear.com/9.x/initials/svg?seed=${userData.email}&backgroundColor=4DA9FF`}
                    alt="Profile"
                  />
                  <OnlineIndicator />
                </AvatarContainer>
                <UserDetails>
                  <UserName>{userData.name}</UserName>
                  <UserEmail>{userData.email}</UserEmail>
                  <UserMeta>
                    <MetaItem>
                      <Shield size={16} />
                      {userData.role ? userData.role.charAt(0).toUpperCase() + userData.role.slice(1) : 'User'}
                    </MetaItem>
                    {userData.organizationName && (
                      <MetaItem>
                        <Building size={16} />
                        {userData.organizationName}
                      </MetaItem>
                    )}
                  </UserMeta>
                </UserDetails>
                <ActionButtons>
                  {!isEditing ? (
                    <Button variant="primary" onClick={() => setIsEditing(true)}>
                      <Edit2 size={18} />
                      Edit Profile
                    </Button>
                  ) : (
                    <ButtonGroup>
                      <Button variant="secondary" onClick={handleCancelEdit}>
                        <X size={16} />
                        Cancel
                      </Button>
                      <Button variant="success" onClick={handleSaveProfile}>
                        <Save size={16} />
                        Save
                      </Button>
                    </ButtonGroup>
                  )}
                </ActionButtons>
              </ProfileInfo>
            </ProfileContent>
          </ProfileHeader>

          <MainContent>
            {/* Left Column */}
            <LeftColumn>
              {/* Personal Information */}
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <User size={24} />
                    Personal Information
                  </SectionTitle>
                </SectionHeader>
                
                <FormGrid>
                  <FormField>
                    <Label>Full Name</Label>
                    {isEditing ? (
                      <Input
                        type="text"
                        value={editedData.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    ) : (
                      <Value>{userData.name || 'Not provided'}</Value>
                    )}
                  </FormField>

                  <FormField>
                    <Label>Email Address</Label>
                    <Value>{userData.email}</Value>
                  </FormField>

                  <FormField>
                    <Label>Phone Number</Label>
                    {isEditing ? (
                      <Input
                        type="tel"
                        value={editedData.phone || ''}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="Enter phone number"
                      />
                    ) : (
                      <Value>{userData.phone || 'Not provided'}</Value>
                    )}
                  </FormField>

                  {userData.timeZone && (
                    <FormField>
                      <Label>Time Zone</Label>
                      <Value>
                        <MapPin size={16} style={{ color: '#1e90ff' }} />
                        {userData.timeZone}
                      </Value>
                    </FormField>
                  )}
                </FormGrid>
              </Section>

              {/* Organization Details */}
              {(userData.organizationName || userData.domain || userData.couponCode) && (
                <Section>
                  <SectionHeader>
                    <SectionTitle>
                      <Building size={24} />
                      Organization Details
                    </SectionTitle>
                  </SectionHeader>
                  
                  <FormGrid>
                    {userData.organizationName && (
                      <FormField>
                        <Label>Organization Name</Label>
                        <Value>{userData.organizationName}</Value>
                      </FormField>
                    )}

                    {userData.domain && (
                      <FormField>
                        <Label>Domain</Label>
                        <Value>
                          <Globe size={16} style={{ color: '#1e90ff' }} />
                          {userData.domain}
                        </Value>
                      </FormField>
                    )}

                    <FormField className="full-width">
                      <Label>Coupon Code</Label>
                      <Value>
                        <Tag size={16} style={{ color: '#1e90ff' }} />
                        {userData.couponCode || 'No coupon code assigned'}
                      </Value>
                    </FormField>
                  </FormGrid>
                </Section>
              )}
            </LeftColumn>

            {/* Right Column */}
            <RightColumn>
              {/* Account Security */}
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <Lock size={20} />
                    Security
                  </SectionTitle>
                </SectionHeader>
                
                <Button 
                  variant="outline" 
                  onClick={() => setIsPasswordModalOpen(true)}
                  className="full-width"
                >
                  <Lock size={16} />
                  Change Password
                </Button>
              </Section>

              {/* Account Status */}
              <Section>
                <SectionHeader>
                  <SectionTitle>
                    <Shield size={20} />
                    Account Status
                  </SectionTitle>
                </SectionHeader>
                
                <div>
                  {userData.role && (
                    <StatusItem>
                      <StatusLabel>Role</StatusLabel>
                      <Badge>{userData.role}</Badge>
                    </StatusItem>
                  )}
                  
                  {userData.lastLogin && (
                    <StatusItem>
                      <StatusLabel>Last Login</StatusLabel>
                      <StatusValue>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#111827' }}>
                          {formatDate(userData.lastLogin)}
                        </div>
                      </StatusValue>
                    </StatusItem>
                  )}
                </div>
              </Section>
            </RightColumn>
          </MainContent>

          {/* Password Modal */}
          {isPasswordModalOpen && (
            <Modal>
              <ModalContent>
                <ModalHeader>
                  <ModalTitle>Change Password</ModalTitle>
                  <CloseButton onClick={closeModal}>
                    <X size={20} />
                  </CloseButton>
                </ModalHeader>

                <div>
                  <PasswordField>
                    <Label>Current Password</Label>
                    <PasswordInput
                      type={isOldPasswordVisible ? 'text' : 'password'}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                    <PasswordToggle onClick={() => setIsOldPasswordVisible(!isOldPasswordVisible)}>
                      {isOldPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </PasswordToggle>
                  </PasswordField>

                  <PasswordField>
                    <Label>New Password</Label>
                    <PasswordInput
                      type={isNewPasswordVisible ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                    <PasswordToggle onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}>
                      {isNewPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </PasswordToggle>
                  </PasswordField>

                  <PasswordField>
                    <Label>Confirm New Password</Label>
                    <PasswordInput
                      type={isConfirmPasswordVisible ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                    <PasswordToggle onClick={() => setIsConfirmPasswordVisible(!isConfirmPasswordVisible)}>
                      {isConfirmPasswordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </PasswordToggle>
                  </PasswordField>
                </div>

                <ButtonGroup>
                  <Button variant="secondary" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={handlePasswordChange}
                    disabled={!oldPassword || !newPassword || !confirmPassword}
                  >
                    Update Password
                  </Button>
                </ButtonGroup>
              </ModalContent>
            </Modal>
          )}

          {/* Toast Container */}
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#111827',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                borderRadius: '0.5rem',
                padding: '1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
              },
              success: {
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#ffffff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#ffffff',
                },
              },
            }}
          />
        </ContentWrapper>
      </Container>
    </>
  );
}

export default ProfilePage;